import { RefObject, useEffect, useState } from "react";

/**
 * Detect whether a `position: sticky` element is currently "stuck" to the
 * top of its scroll container, so callers can style it only while pinned
 * (e.g. show a bottom border that appears once content scrolls underneath
 * but is absent when the element sits in its natural position).
 *
 * Works via a zero-cost sentinel placed immediately *above* the sticky
 * element: while the sentinel is visible inside the scroll container the
 * element is in its natural spot (not stuck); once the sentinel scrolls
 * out past the top edge the element is pinned. The scroll container is
 * resolved by walking up the DOM to the nearest scrollable ancestor, so
 * we don\'t depend on any CSS-module class name.
 *
 * `sentinelRef` must point at a 1px-tall element rendered directly before
 * the sticky element (see `.scenarios-sticky-sentinel`).
 */
export const useStuckToTop = (
    sentinelRef: RefObject<HTMLElement | null>,
    enabled: boolean
): boolean => {
    const [stuck, setStuck] = useState(false);
    useEffect(() => {
        if (!enabled) {
            setStuck(false);
            return;
        }
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        // Nearest scrollable ancestor = the IntersectionObserver root.
        let root: HTMLElement | null = sentinel.parentElement;
        while (root) {
            const overflowY = getComputedStyle(root).overflowY;
            if (overflowY === "auto" || overflowY === "scroll") break;
            root = root.parentElement;
        }

        const observer = new IntersectionObserver(
            ([entry]) => setStuck(!entry.isIntersecting),
            { root, threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [sentinelRef, enabled]);
    return stuck;
};
